"use client";

import React, { useEffect, useRef, useState } from "react";
import { Compass, Target, Rocket, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "01",
  title: "Pay",
  description: "Select your plan and complete our secure checkout to instantly provision your private AI environment and secure workspace.",
  icon: Compass
}, {
  id: "02",
  title: "Onboard",
  description: "Receive an automated activation message on iMessage or Slack within seconds of checkout to link your private conversation.",
  icon: Target
}, {
  id: "03",
  title: "Go",
  description: "Start interacting with high-scale AI capabilities immediately. No apps, no APIs, and absolutely no learning curve required.",
  icon: Rocket
}, {
  id: "04",
  title: "Scale",
  description: "Unlock custom enterprise deployments and private on-premise installations as your organizational security requirements evolve.",
  icon: Trophy
}];
function StepItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="145357" ref={ref} data-index={index} className={cn("relative flex flex-col md:flex-row items-center gap-4 md:gap-16 transition-all duration-1000 ease-out", !isEven && "md:flex-row-reverse", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center justify-center z-10">
        <div className="w-4 h-4 rounded-full bg-primary ring-4 ring-background shadow-md" />
      </div>

      <div className={cn("flex-1 w-full flex flex-col", isEven ? "md:items-end md:text-right" : "md:items-start md:text-left")}>
        <div className="max-w-md space-y-4">
          <Badge variant="outline" className="mb-2 text-sm px-3 py-1 border-primary/30 text-primary bg-primary/10 font-default">
            Step {step.id}
          </Badge>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground font-default">
            {step.title}
          </h3>
          <p className="text-muted-foreground leading-relaxed text-lg font-default">
            {step.description}
          </p>
        </div>
      </div>

      <div className="flex-1 w-full flex justify-center">
        <div className="relative w-full max-w-sm aspect-square rounded-3xl bg-card border border-border/40 flex items-center justify-center overflow-hidden group hover:border-primary/50 transition-colors duration-500 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-50" />
          <div className="absolute -inset-24 bg-primary/5 blur-3xl rounded-full group-hover:bg-primary/15 transition-colors duration-500" />
          
          <div className="relative z-10 w-24 h-24 rounded-2xl bg-background shadow-2xl border border-border/60 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500 ease-out">
            <Icon className="w-10 h-10 text-primary" strokeWidth={1.2} />
          </div>
        </div>
      </div>
    </div>;
}
export default function TheIntegrationJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-path-to-entry" className="dark bg-background py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
            The Path to Entry
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Experience full-scale AI capability in under a minute with our streamlined activation process designed for immediate impact.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 top-8 bottom-8 w-px border-l-2 border-dashed border-primary/20 -translate-x-1/2 hidden md:block opacity-40" />

          <div className="space-y-12 md:space-y-32">
            {steps.map((step, i) => <StepItem key={step.id} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}